import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { ExperimentalFeaturesComponent } from './core/components/experimental-features/experimental-features.component';
import { MaintenanceComponent } from './core/components/maintenance/maintenance.component';
import { PermissionDeniedComponent } from './core/components/permission-denied/permission-denied.component';
import { AdminGuard } from './core/guards/admin/admin.guard';
import { AuthenticationGuard } from './core/guards/authentication-guard';
import { ExperimentalFeaturesGuard } from './core/guards/experimental-features/experimental-features.guard';
import { LandingPageGuard } from './core/guards/landing-page/landingpage.guard';
import { ImplicitLoginComponent } from './shared/components/implicit-login/implicit-login.component';
import { ImplicitSignupComponent } from './shared/components/implicit-signup/implicit-signup.component';
import { LandingpagePlaceholderComponent } from './shared/components/landingpage-placeholder/landingpage-placeholder.component';
import {
    APPLICATIONS_PATH,
    PERMISSION_APPLICATIONS,
    PERMISSION_MACHINELEARNING,
    PERMISSION_MACHINELEARNING_ANNOTATE,
    PERMISSION_MACHINELEARNING_OVERVIEW,
    PERMISSION_MACHINELEARNING_SEGMENT,
    PERMISSION_MACHINELEARNING_TRAIN,
    PERMISSION_MYLAB,
    PERMISSION_MYLAB_FILES,
    PERMISSION_MYLAB_MODULES,
    PERMISSION_MYLAB_RESULTS,
    PERMISSION_MYLAB_WORKFLOWS,
} from './shared/constants/constants';

const routes: Routes = [
    {
        path: 'home',
        component: LandingpagePlaceholderComponent,
        canActivate: [LandingPageGuard],
    },
    {
        path: APPLICATIONS_PATH,
        loadChildren: () => import('./features/applications/applications.module').then(m => m.ApplicationsModule),
        data: {
            requiredPermissions: [PERMISSION_APPLICATIONS],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'maintenance',
        component: MaintenanceComponent,
    },
    {
        path: 'user/workspace/modules',
        loadChildren: () => import('./features/my-modules/my-modules.module').then(m => m.MyModulesModule),
        data: {
            requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_MODULES],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'user/workspace/files',
        loadChildren: () => import('./features/my-files/my-files.module').then(m => m.MyFilesModule),
        data: {
            requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_FILES],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'user/workspace/data',
        loadChildren: () => import('./features/MED-data/MED-files.module').then(m => m.MEDFilesModule),
        data: {
            requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_FILES],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'user/workspace/workflows',
        loadChildren: () => import('./features/my-workflows/my-workflows.module').then(m => m.MyWorkflowsModule),
        data: {
            requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_WORKFLOWS],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'user/workspace/results',
        loadChildren: () => import('./features/my-results/my-results.module').then(m => m.MyResultsModule),
        data: {
            requiredPermissions: [PERMISSION_MYLAB, PERMISSION_MYLAB_RESULTS],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'user',
        loadChildren: () => import('./features/user/user.module').then(m => m.UserModule),
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'modules',
        loadChildren: () => import('./features/public-modules/public-modules.module').then(m => m.PublicModulesModule),
    },
    {
        path: 'ai-toolkit/overview',
        loadChildren: () => import('./features/ml-overview/ml-overview.module').then(m => m.MLOverviewModule),
        data: {
            requiredPermissions: [PERMISSION_MACHINELEARNING_OVERVIEW],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'ai-toolkit/my-datasets',
        loadChildren: () => import('./features/ml-annotate/ml-annotate.module').then(m => m.MLAnnotateModule),
        data: {
            requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_ANNOTATE],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'ai-toolkit/my-models',
        loadChildren: () => import('./features/ml-train/ml-train.module').then(m => m.MLTrainModule),
        data: {
            requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_TRAIN],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'machine-learning/train',
        loadChildren: () => import('./features/ml-train/ml-train.module').then(m => m.MLTrainModule),
        data: {
            requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_TRAIN],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'ai-toolkit/my-segmentations',
        loadChildren: () => import('./features/ml-segment/ml-segment.module').then(m => m.MLSegmentModule),
        data: {
            requiredPermissions: [PERMISSION_MACHINELEARNING, PERMISSION_MACHINELEARNING_SEGMENT],
        },
        canLoad: [AuthenticationGuard],
        canActivate: [AuthenticationGuard],
        canActivateChild: [AuthenticationGuard],
    },
    {
        path: 'workflows',
        loadChildren: () => import('./features/workflows/workflows.module').then(m => m.WorkflowsModule),
    },
    {
        path: 'admin',
        loadChildren: () => import('./features/admin/admin.module').then(m => m.AdminModule),
        canLoad: [AdminGuard],
    },
    {
        path: 'results',
        loadChildren: () => import('./features/results/results.module').then(m => m.ResultsModule),
    },
    {
        path: 'permission-denied',
        component: PermissionDeniedComponent,
    },
    {
        path: 'implicit-login',
        component: ImplicitLoginComponent,
    },
    {
        path: 'implicit-signup',
        component: ImplicitSignupComponent,
    },
    {
        path: 'experimental-features',
        component: ExperimentalFeaturesComponent,
        canLoad: [AdminGuard],
    },
    {
        path: 'component-showcase',
        loadChildren: () => import('./features/component-showcase/component-showcase.module').then(m => m.ComponentShowcaseModule),
        canLoad: [ExperimentalFeaturesGuard],
    },
    { path: '', pathMatch: 'full', redirectTo: 'home' },
    { path: '**', redirectTo: 'home' },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            preloadingStrategy: PreloadAllModules,
            scrollPositionRestoration: 'top',
            enableTracing: false,
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
